import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Asegúrate que la ruta sea correcta
import useNotifications from '../hooks/useNotifications'; // Ajusta la ruta según donde lo guardaste

// 🔥 1. HELPER PARA TIEMPO REAL (Copiado de App.js)
const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    let date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return `hace ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours}h`;
    const days = Math.floor(hours / 24);
    return `hace ${days}d`;
};

// =========================================================
// NotificationItem (ACTUALIZADO)
// =========================================================
const NotificationItem = ({ notification, onClick, onDelete }) => {
    const isUnread = !notification.isRead;
    const [actorProfile, setActorProfile] = useState(null);

    const [showOptions, setShowOptions] = useState(false);
    const optionsRef = useRef(null);

    useEffect(() => {
        const fetchActor = async () => {
            if (notification.actors && notification.actors.length > 0) {
                const mainActorId = notification.actors[0];
                try {
                    const userDoc = await getDoc(doc(db, "users", mainActorId));
                    if (userDoc.exists()) setActorProfile(userDoc.data());
                } catch (e) { console.error(e); }
            }
        };
        fetchActor();
    }, [notification.actors]);

    // Cerrar el menú si hago click fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (optionsRef.current && !optionsRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };
        if (showOptions) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showOptions]);

    const getNotificationContent = () => {
        const { type, actorCount, previewText } = notification;
        const name = actorProfile ? actorProfile.displayName : "Un usuario";
        const othersCount = actorCount > 1 ? actorCount - 1 : 0;

        switch (type) {
            case 'like_story':
                return <span>A <b>{name}</b> {othersCount > 0 && `y ${othersCount} más`} les gustó tu historia <i>{previewText}</i>.</span>;
            case 'like_chapter':
                return <span>A <b>{name}</b> {othersCount > 0 && `y ${othersCount} más`} les gustó el capítulo <i>{previewText}</i>.</span>;
            case 'comment_story': // Comentario general a historia (si existiera)
            case 'comment_chapter': // Comentario específico a capítulo
                 return <span><b>{name}</b> {othersCount > 0 && `y ${othersCount} más`} comentaron en el capítulo: "{previewText}".</span>;
            case 'comment_profile':
                 return <span><b>{name}</b> {othersCount > 0 && `y ${othersCount} más`} te dejaron un mensaje: "{previewText}".</span>;
            case 'follow':
                return <span><b>{name}</b> {othersCount > 0 && `y ${othersCount} más`} comenzaron a seguirte.</span>;
            case 'presave_release':
                return <span className="text-pink-400 font-bold">¡ESTRENO! El capítulo "{previewText}" que guardaste ya está disponible.</span>;
            case 'system':
                return <span className="text-yellow-400 font-medium">SISTEMA: {previewText}</span>;
            default: return <span>Nueva interacción.</span>;
        }
    };

    const getIconOrAvatar = () => {
        if (notification.type === 'system') return '⚠️';
        if (notification.type === 'presave_release') return '🔥';
        if (actorProfile && actorProfile.avatarURL) {
            return <img src={actorProfile.avatarURL} alt="Avatar" className="w-10 h-10 rounded-full object-cover border border-gray-600" />;
        }
        return '🔔';
    };

    return (
        <div 
            onClick={() => onClick(notification, actorProfile)}
            // Usamos items-center para centrar verticalmente todo el bloque
            className={`w-full group flex items-center p-3 border-b border-gray-700 hover:bg-gray-700/50 cursor-pointer transition-colors ${isUnread ? 'bg-gray-800/80' : 'bg-transparent opacity-70'}`}
        >
            {/* COLUMNA 1: AVATAR (Izquierda) */}
            <div className="flex-shrink-0 mr-2 relative w-10 h-10 flex items-center justify-center bg-gray-700 rounded-full text-lg">
                {typeof getIconOrAvatar() === 'string' ? getIconOrAvatar() : getIconOrAvatar()}
            </div>

            {/* 2. Contenido (Centro) */}
            <div className="flex-grow text-sm text-gray-200 min-w-10 content_noti">
                <p className="break-words leading-snug">{getNotificationContent()}</p>
                <p className="text-xs text-gray-500 mt-1 font-medium">{formatTimestamp(notification.lastUpdatedAt)}</p>
            </div>

            {/* 3. Acciones (Derecha - Estilo Facebook) */}
            <div className="flex-shrink-0 flex items-center gap-1" ref={optionsRef}>
                
                {/* Botón de Opciones (...) - Solo visible en Hover o si el menú está abierto */}
                <div className={`relative ${showOptions ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // ¡Vital!
                            setShowOptions(!showOptions);
                        }}
                        className={`p-2 rounded-full hover:bg-gray-600 text-gray-400 hover:text-white transition-colors ${showOptions ? 'bg-gray-600 text-white' : ''}`}
                    >
                        {/* Icono de 3 puntitos */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                    </button>

                    {/* Dropdown Menú de Eliminar */}
                    {showOptions && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-600 rounded-md shadow-xl z-50 overflow-hidden animate-fadeIn">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(notification.id, notification.type);
                                    setShowOptions(false);
                                }}
                                className="w-full text-left px-4 py-3 text-sm text-gray-200 hover:bg-red-900/30 hover:text-red-400 flex items-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Eliminar notificación
                            </button>
                        </div>
                    )}
                </div>

                {/* Puntito Azul (Siempre centrado y a la derecha de todo) */}
                <div className="w-3 flex justify-center">
                    {isUnread && (
                        <div className="w-3 h-3 bg-blue-500 rounded-full unread_noti shadow-[0_0_8px_rgba(59,130,246,0.8)]"></div>
                    )}
                </div>

            </div>
        </div>
    );
};

// =========================================================
// NotificationBell (MANEJADOR DE CLICS CORREGIDO)
// =========================================================
const NotificationBell = ({ userProfile }) => {
    const { notifications, unreadCount, loading, markAllAsRead, markAsRead, deleteNotification } = useNotifications(userProfile);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // 🔥 3. LÓGICA DE NAVEGACIÓN MAESTRA
    const handleNotificationClick = (notif, actorProfile) => {
        markAsRead(notif.id);
        setIsOpen(false);

        switch (notif.type) {
            case 'like_story':
                // targetId es storyId
                navigate(`/story/${notif.targetId}`);
                break;

            case 'like_chapter':
            case 'comment_chapter':
                // targetId es chapterId. Necesitamos storyId.
                // Si el backend ya lo guardó (nuevo fix), lo usamos.
                if (notif.storyId) {
                    navigate(`/story/${notif.storyId}/chapter/${notif.targetId}`);
                } else {
                    // Fallback si es notificación vieja: intentamos ir a la historia, 
                    // asumiendo que targetId podría ser storyId por error, o avisamos.
                    console.warn("Falta storyId en notificación antigua.");
                    // Opción: Ir al dashboard o intentar navegar solo con chapterId si tu router lo soporta (no lo soporta)
                    navigate('/dashboard'); 
                }
                break;

            case 'follow':
                // targetId es el UID del seguidor.
                // Necesitamos el USERNAME para la URL. Lo sacamos del actorProfile que pasamos desde el Item.
                if (actorProfile && actorProfile.username) {
                    navigate(`/profile/${actorProfile.username}`);
                }
                break;

            case 'comment_profile':
                // targetId es tu propio UID (o el del dueño).
                // Te lleva a TU perfil.
                navigate(`/profile/${userProfile.username}`);
                // Scroll al div de comentarios (puedes añadir id="comments-section" en ProfilePage)
                setTimeout(() => {
                    const el = document.getElementById('comments-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 500);
                break;

            case 'presave_release':
                if (notif.storyId) {
                    navigate(`/story/${notif.storyId}/chapter/${notif.targetId}`);
                }
                break;
                
            default:
                break;
        }
    };

    const handleBellClick = () => {
        if (window.innerWidth < 768) navigate('/notifications');
        else setIsOpen(!isOpen);
    };

    if (!userProfile) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            <button onClick={handleBellClick} className="relative p-2 text-gray-400 hover:text-white transition-colors focus:outline-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-gray-800">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>
            {isOpen && (
                <div className="hidden md:block absolute right-0 mt-2 bg-gray-800 MENU_NOTIS shadow-2xl z-50 overflow-hidden border border-gray-700 origin-top-right animate-fadeIn">
                    <div className="p-3 border-b border-gray-700 flex justify-between items-center">
                        <h3 className="font-bold text-white">Notificaciones</h3>
                        {unreadCount > 0 && <button onClick={markAllAsRead} className="text-xs text-indigo-400 hover:underline">Marcar leídas</button>}
                    </div>
                    <div className="max-h-80 overflow-y-auto custom-scrollbar">
                        {loading ? <div className="p-4 text-center text-gray-900 text-sm TEXT_NOTIS">Cargando...</div> : notifications.length > 0 ? (
                            notifications.map(notif => <NotificationItem key={notif.id} notification={notif} onClick={handleNotificationClick} onDelete={deleteNotification} />)
                        ) : <div className="p-8 text-center text-gray-500"><p className="text-sm">No tienes notificaciones nuevas.</p></div>}
                    </div>
                    <div className="p-2 border-t border-gray-700 text-center">
                        <Link to="/notifications" onClick={() => setIsOpen(false)} className="text-xs font-bold text-gray-400 hover:text-white uppercase tracking-wider">Ver Todo</Link>
                    </div>
                </div>
            )}
        </div>
    );
};

// =========================================================
// 3. COMPONENTE PÁGINA: NotificationsPage (Móvil/Full)
// =========================================================
const NotificationsPage = ({ userProfile }) => {
    const { notifications, loading, markAllAsRead, markAsRead, deleteNotification } = useNotifications(userProfile);
    const navigate = useNavigate();

    const handleNotificationClick = (notif, actorProfile) => {
        markAsRead(notif.id);
        if (notif.type === 'like_story') navigate(`/story/${notif.targetId}`);
        else if (notif.type === 'like_chapter' || notif.type === 'comment_chapter') {
            if (notif.storyId) navigate(`/story/${notif.storyId}/chapter/${notif.targetId}`);
        }
        else if (notif.type === 'follow' && actorProfile?.username) navigate(`/profile/${actorProfile.username}`);
        else if (notif.type === 'comment_profile') {
             navigate(`/profile/${userProfile.username}`);
             // Scroll logic...
        }
        else if (notif.type === 'presave_release' && notif.storyId) navigate(`/story/${notif.storyId}/chapter/${notif.targetId}`);
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white pb-20">
            <div className="sticky top-0 z-30 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center shadow-md">
                <h1 className="text-xl font-bold">Notificaciones</h1>
                <button onClick={markAllAsRead} className="text-sm text-indigo-400 font-medium">Leídas</button>
            </div>
            <div className="max-w-2xl w-full mx-auto">
                {loading ? <p className="p-4 text-center text-gray-500">Cargando...</p> : notifications.length > 0 ? (
                    notifications.map(notif => <NotificationItem key={notif.id} notification={notif} onClick={handleNotificationClick} onDelete={deleteNotification} />)
                ) : <div className="flex flex-col items-center justify-center py-20 text-gray-500"><p>No tienes notificaciones recientes.</p></div>}
            </div>
        </div>
    );
};

export { NotificationBell, NotificationsPage };